import React, { useEffect, useId, useState } from "react";
import { Button } from "react-bootstrap";
import CodeInput from "./CodeInput";
import MethodPicker from "./MethodPicker";
import TinOfferBox, { nextSteps } from "./TinOfferBox";
import "./mfaVerifyStep.css";

const HINTS = {
  sms: "We texted you a 6-digit code.",
};
const SENT_BY_US = ["email", "sms"];

// docs/specs/mfa-ux-cleanup.md §3 (Row 1, option B): the reason for SSN/TIN, in a blue box above the field.
// docs/specs/login-setup.md §1, §3: names the channel that failed (`failed` = the sign-in's default_method) and the
// setup to come; useMfaLogin fills both only with LOGIN_SETUP on, so flag off reads as today.
const TinWhyBox = ({ failed, setupRequired }) => {
  const headingId = useId();
  const missing = failed === "sms" ? "Your text didn't arrive." : "Your email code didn't arrive.";
  return (
    <div className="mfa-tin-why" role="note" aria-labelledby={headingId}>
      <strong id={headingId}>Why are we asking for your SSN or TIN?</strong>
      <p>
        {`${missing} To keep your loan account safe, we check the last 4 digits of the SSN or TIN on your loan instead. `}
        {`After that you'll ${nextSteps(setupRequired)} for future sign-ins.`}
      </p>
    </div>
  );
};

// Presentational only: all state and calls come from useMfaLogin via props.
const MfaVerifyStep = ({
  methods, method, emailMasked, phoneMasked, tinWaitSeconds, busy, error, expired, resendBlocked,
  defaultMethod, setupRequired, onSelectMethod, onSubmit, onRestart, onResend, afterSubmit,
}) => {
  const [code, setCode] = useState("");
  useEffect(() => setCode(""), [method]);

  if (expired) {
    return (
      <div>
        {error && <div className="alert alert-error" role="alert">{error}</div>}
        <Button className="w-100" onClick={onRestart}>Sign in again</Button>
      </div>
    );
  }

  const hint = {
    email: `We emailed a 6-digit code to ${emailMasked}.`,
    sms: phoneMasked ? `We texted a 6-digit code to ${phoneMasked}.` : HINTS.sms,
  }[method] ?? HINTS[method];
  // The server caps sends per sign-in: once it refused one, a line replaces the resend link.
  const sentByUs = SENT_BY_US.includes(method);
  // On a code we sent, SSN/TIN is offered in its own box (spec §2), so the picker leaves it out.
  const tinOffer = sentByUs && methods.includes("tin");
  const pickerMethods = tinOffer ? methods.filter((m) => m !== "tin") : methods;
  return (
    <div>
      {hint && <p className="text-muted small mb-2">{hint}</p>}
      {method === "tin" && <TinWhyBox failed={defaultMethod} setupRequired={setupRequired} />}
      <CodeInput method={method} value={code} onChange={setCode} disabled={busy}
        onResend={sentByUs && !resendBlocked ? onResend : undefined} />
      {sentByUs && resendBlocked && (
        <p className="mfa-resend text-muted small">No more codes can be sent for this sign-in.</p>
      )}
      {error && <div className="alert alert-error mt-2" role="alert">{error}</div>}
      <Button className="w-100 mt-3 mb-2 text-white fw-bold" type="button" disabled={busy} onClick={() => onSubmit(code)}>
        {method === "tin" ? "Verify and Continue" : "Verify OTP and Continue"}
      </Button>
      {afterSubmit}
      {tinOffer && (
        <TinOfferBox method={method} tinWaitSeconds={tinWaitSeconds} onSelect={onSelectMethod} disabled={busy}
          setupRequired={setupRequired} />
      )}
      <MethodPicker methods={pickerMethods} method={method} onSelect={onSelectMethod} disabled={busy}
        tinWaitSeconds={tinWaitSeconds} />
    </div>
  );
};

export default MfaVerifyStep;
